<template>
  <!-- 变量提取设置
       @author: liuguisheng
       @date: 2019-9-10
  -->
  <div class="setting-item">
    <el-form-item label="变量设置">
      <!-- 
        1. 自定义筛选方法，记录输入内容，自定义过滤
        2. 下拉收起，清空筛选内容
       -->
      <el-select v-model="currentItemVariable"
                 class="setting-varlable"
                 multiple 
                 filterable 
                 placeholder="请选择"
                 :filter-method="(filterStr) => selectFilterValue = filterStr"
                 @visible-change="arguments[0] || (selectFilterValue = '')">
        <el-option v-for="item in varRelatedOptions"
                   :key="item.value"
                   :label="item.label"
                   :value="item.value"></el-option>
      </el-select>
    </el-form-item>
  </div>
</template>

<script>
// 导入value监听混入
import mixValueWatch from "@/mixins/common/mix_value_watch.js";
// 导入计算属性映射方法
import { mapComputed } from "@/mixins/common/mix_value_watch.js";
// 首页子组件混入
import { mixRiceIndexChild } from "@/mixins/rice_index/mix_rice_index_child.js";

/**
 * 变量提取实现思路：
 * 
 * 1.首先建立一个变量字典，用来存储所有的变量对应关系
 * 2.然后将当前表单项设置的变量设置作为一条数据存入变量字典，key为当前表单项model，值为所有变量集合(包含
 *   当前表单项本身)，同时，将集合中所有的变量也作为key,存入变量字典，为的就是切换表单项时，将变量对应设置带过来。
 * 3.在渲染变量设置时，根据该表单项model去变量字典中查询其配置，该表单项变量配置是变量字典中找到的数据，然后渲染时去掉其本身
 * 4.当组件预览时，监听该组件model变化，更新表单数据（formModels）
 * 5.删除表单项时，需要把变量字典中有关的设置删除
 * 
 * 需求变动：
 * 变量关联只建立单向关联，比如a关联b， b不再关联a,（之前是b也关联a）先这么实现，等待用户反馈
*/

export default {
  mixins: [mixValueWatch, mixRiceIndexChild],
  data () {
    return {
      // 选择项筛选内容
      selectFilterValue: ""
    }
  },
  computed: {
    /** @name 变量提取设置
     *  @description 将 variable 字段映射到当前 variable 计算属性
     *  @return {String}
     *  @author liuguisheng
     *  @version 2020-1-7
     */
    ...mapComputed({   
      variable: {
        parent: i => i.baseOption,
        attr: "variable"
      }
    }),
    /** @name 当前表单项model计算
     *  @return  {String} 当前表单项model
     *  @author liuguisheng
     *  @version 2020-1-7
     */
    model () {
      return this.value.baseOption.model
    },
    /** @name 变量关联表单选择项
     *  @return {Array} 表单选择项
     *  @author 王汝鹏
     *  @version 2020-01-13
     */
    varRelatedOptions() {
      // 下拉选择项，选择项最大长度
      let options = [];
      let maxLength = 20;
      // 当前表单项类型，当前表单项id
      let { type:currentItemType, selfId:currentItemId } = this.value.node;

      for (let formId in this.formMap) {
        let { node, baseOption } = this.formMap[formId];
        let { type, selfId } = node;
        let { customName, model } = baseOption;
        // 当前表单项不放入选择项中
        if (selfId === currentItemId) continue;
        // 如果没有过滤表单项(加载时)，将已经选择的选项添加到选择项中(如果没有加入，加载时会显示选中的值)
        if ((this.selectFilterValue==='') && this.currentItemVariable.includes(model)) { options.push({ label:customName, value:model }); continue; }
        // 如果大于选择项的最大长度不再进行放入
        if (options.length >= maxLength) continue;
        // 类型与当前表单一样，并且表单名称包含筛选内容，将表单项放入选择项中
        if ((type === currentItemType) && customName.includes(this.selectFilterValue)) options.push({ label:customName, value:model });
      }

      return options;
    },
    /** @name 计算当前表单项变量设置
     *  @return  { Array } 当前表单项设置的变量
     *  @author liuguisheng
     *  @version 2020-1-7
     */
    currentItemVariable: {
      get () {
        /**
         * 加载变量设置时，根据当前表单项model去变量字典中查找当前变量设置，
         * 如果不存在返回空数组，如果存在证明设置过变量对应，因为在变量字典中
         * 保存的变量包含其本身，所以将过滤掉
        */
        let { variableMap, model } = this;
        let modelKeyList = Object.keys(variableMap);
        if (Reflect.has(variableMap, model) === false) return [];
        return this.variableMap[model].filter(el => el != model);
      },
      set ( newValue ) {
        let { model, variableMap } = this;
        // 向变量字典中添加当前变量设置（当前设置包含其本身，为的是在预览时好处理），如果当前变量设置的值小于两个，证明该项设置无效，从字典中删除
        variableMap[model] = [...new Set(newValue).add(model)]
        variableMap[model].length < 2 && delete variableMap[model];
        this.variableMap = {...variableMap}

        /***********************************
         * 
         *  保留之前的变量之间双向关联的需求
         * 
         ***********************************/
        // 当前设置所有有关的变量，包含其本身
        // let newItemVariable = new Set(newValue).add(this.model);
        // // 之前设置的变量
        // let oldItemVariable = new Set(this.variableMap[model]);
        // // 新老数据对比判断是添加选项还是删除选项
        // let edit = newItemVariable.size > oldItemVariable.size ? 'add' : 'delete';
        // console.log('当前model',model)
        // if ( edit == 'delete' ) {
        //   // 新老数据取差集，从老数据中找到被删除的元素
        //   let deleteItem = [...oldItemVariable].filter( item => !newItemVariable.has(item))[0];
        //   this.variableMap[deleteItem] = this.variableMap[deleteItem].filter(item => item != model)
        //   this.variableMap[model] = this.variableMap[model].filter(item => item != deleteItem)
        // } else {
        //   // 添加的元素
        //   // let addItem = [...newItemVariable].filter( item => !oldItemVariable.has(item))[0];
        //   // 添加对应变量配置
        //   newItemVariable.forEach(el => this.variableMap[el] = [...newItemVariable]);
        // }
      }
    }
  }
};
</script>


// WEBPACK FOOTER //
// src/components/Setting/base_setting/SettingVariable.vue